import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { API } from '../config';
import Navigation from '../components/Navigation';
import NewsletterSignup from '../components/NewsletterSignup';
import SiteFooter from '../components/SiteFooter';

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory]);

  const fetchArticles = async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/articles?published_only=true`
        : `${API}/articles?category=${selectedCategory}&published_only=true`;
      
      const response = await fetch(url);
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const [categories, setCategories] = useState([{ id: 'all', name: 'All Articles', icon: '📚' }]);

  const iconMap = {'Bus': '🚌', 'CreditCard': '💳', 'Eye': '👁️', 'Users': '👨\u200d👩\u200d👧\u200d👦', 'GraduationCap': '🎓', 'Tag': '🏷️', 'Heart': '❤️', 'Shield': '🛡️', 'PiggyBank': '💰', 'TrendingUp': '📈', 'Calendar': '📅', 'Dumbbell': '🏋️'};

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const [catRes, activeRes] = await Promise.all([
          fetch(`${API}/categories`),
          fetch(`${API}/articles/categories/list`)
        ]);
        const catData = await catRes.json();
        const activeData = await activeRes.json();
        const activeSlugs = activeData.categories.map(c => c.name);
        const filtered = catData.categories
          .filter(c => activeSlugs.includes(c.slug))
          .map(c => ({ id: c.slug, name: c.name, icon: iconMap[c.icon] || '📄' }));
        setCategories([{ id: 'all', name: 'All Articles', icon: '📚' }, ...filtered]);
      } catch (e) {
        console.error('Failed to fetch categories:', e);
      }
    };
    fetchCats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
      <SEO title="Benefits Knowledge Center" description="Expert guides on healthcare, 401k matching, HSA plans, credit cards, and employee benefits. Learn how to maximize your total compensation package." path="/articles" keywords="employee benefits guides, 401k match explained, HSA eligible health plans, health insurance from employer, employee benefits checklist, open enrollment tips" />
      
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Header */}
      <section className="bg-gradient-hero py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Benefits Knowledge Center</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
            Expert guides to help you maximize your employee benefits
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-gray-50 border-b border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-600 hover:text-blue-600'
                }`}
              >
                <span className="mr-2">{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">No articles found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map(article => (
                <article
                  key={article.id}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-blue-600 hover:shadow-xl transition-all hover-lift cursor-pointer"
                  onClick={() => window.location.href = `/articles/${article.slug}`}
                >
                  {/* Category Badge */}
                  <div className="p-6 pb-0">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-semibold uppercase tracking-wide">
                      {article.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                      {article.title}
                    </h2>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    
                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {article.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Read More */}
                    <div className="flex items-center text-blue-600 font-semibold group">
                      Read Article
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-sm text-gray-500">
                    By {article.author}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
